import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { getUsers } from "apis/adminServices/userManagmentService";
import BlockUserModal from "./blockUserModal";
import { useGlobalLoader } from "contexts/GlobalLoaderContext";
import { User } from "./type";
import UserFilterDropdown from "./userFilterDropdown";

const UserManagement: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]); // Store users list
  const [loading, setLoading] = useState(true); // Loading state
  const [currentPage, setCurrentPage] = useState(1); // Current page for pagination
  const [pageSize, setPageSize] = useState(10); // Number of items per page
  const [totalUsers, setTotalUsers] = useState(0); // Total number of users
  const [searchTerm, setSearchTerm] = useState(""); // Search term
  const [filter, setFilter] = useState(""); // Filter term
  const { showLoader, hideLoader } = useGlobalLoader();
  const [isFilterOpen, setIsFilterOpen] = useState<boolean>(false);

  // New state for block user modal
  const [selectedUser, setSelectedUser] = useState<User | null>(null);

  const fetchUsers = async (
    page: number,
    size: number,
    showGlobalLoader = true
  ) => {
    try {
      setLoading(true);
      if (showGlobalLoader) showLoader();
      const response = await getUsers(searchTerm, filter, page, size);
      setUsers(response?.items);
      setTotalUsers(response?.totalCount); // Assuming the API returns total count
    } catch (error) {
      console.error("Error fetching users:", error);
    } finally {
      hideLoader();
      setLoading(false);
    }
  };

  // Improved filter change handler
  const handleFilterChange = (filters: { status?: string }) => {
    // Update the filter state based on the filters passed from UserFilterDropdown
    setFilter(filters.status || "");
    setCurrentPage(1); // Reset to first page when applying filters
    setIsFilterOpen(false); // Close the filter dropdown
  };

  useEffect(() => {
    // If user is typing a search term, skip showing global loader
    const isSearch = searchTerm.trim().length > 0;
    fetchUsers(currentPage, pageSize, !isSearch);
  }, [currentPage, pageSize, searchTerm, filter]);

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1); // Reset to first page when searching
  };

  const handleBlockUser = (userId: number) => {
    console.log(`Blocking user with ID: ${userId}`);
    // Implement block user logic
  };

  const openBlockUserModal = (user: User) => {
    setSelectedUser(user);
    console.log(user);
  };

  const closeBlockUserModal = () => {
    setSelectedUser(null);
  };

  // Calculate total pages
  const totalPages = Math.ceil(totalUsers / pageSize);

  // Generate page numbers to display
  const generatePageNumbers = () => {
    const pages: number[] = [];
    const maxPagesToShow = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxPagesToShow / 2));
    const endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);

    // Adjust start page if we're near the end
    if (endPage === totalPages) {
      startPage = Math.max(1, totalPages - maxPagesToShow + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }

    return pages;
  };

  return (
    <div className="px-6 pt-6">
      <section>
        <div className="bg-white rounded-xl p-4 mt-2">
          <div className="flex flex-wrap gap-4 items-center justify-between pb-4">
            <div>
              <h1 className="text-xl font-semibold text-grease-100">User Management</h1>
              <nav className="flex items-center gap-2 text-sm mt-2" aria-label="Breadcrumb">
                <span className="st-dashboard text-lightgray-200 text-sm" aria-hidden="true" />
                <Link to="/admin/dashboard" className="hover:text-royalblue-100 transition-colors duration-150">
                  Dashboard
                </Link>
                <span className="st-arrow-right text-lightgray-200 text-xs" aria-hidden="true" />
                <span className="text-royalblue-100 font-medium">User Management</span>
              </nav>
            </div>
            <div className="flex flex-wrap items-center gap-3 relative">
              <div className="relative">
                <input
                  className="block w-full p-3 pl-10 rounded-lg bg-lightblue-100 text-sm text-grease-100 placeholder-gray-400 focus:outline-none"
                  placeholder="Search by keyword"
                  value={searchTerm}
                  onChange={handleSearch}
                />
                <button className="absolute w-fit inset-0 end-0 flex items-center z-20 px-3 cursor-pointer text-gray-400 rounded-e-md focus:outline-none">
                  <i className="st-search text-lightgray-200"></i>
                </button>
              </div>
              <div className="relative">
                {/* Button to Open Filter */}
                <button
                  id="dropdownDefaultButton-3"
                  data-dropdown-toggle="dropdown-3"
                  className="flex gap-2 p-3 border bg-[#FFD700] rounded-lg"
                  onClick={() => setIsFilterOpen(true)}
                >
                  <i className="st-filter text-white"></i>
                  <p className="font-medium text-white text-sm">Filters</p>
                </button>

                {/* Render UserFilterDropdown if open */}
                <UserFilterDropdown onClose={() => setIsFilterOpen(false)} onFilterChange={handleFilterChange} isOpen={isFilterOpen} />
              </div>
            </div>
          </div>

          {/* Table remains the same as in the original component */}
          <div className="relative overflow-x-auto sm:rounded-lg">
            <table className="w-full text-sm text-left text-gray-500">
              <thead className="text-xs text-gray-700 p-4 bg-blue-50">
                <tr>
                  <th scope="col" className="px-6 py-4 rounded-l-lg">
                    <p className="text-lightgray-200">User ID</p>
                  </th>
                  <th scope="col" className="px-6 py-4">
                    <p className="border-l-2 border-lightgray-50 text-lightgray-200 px-4">
                      User Name
                    </p>
                  </th>
                  <th scope="col" className="px-6 py-4">
                    <p className="border-l-2 border-lightgray-50 text-lightgray-200 px-4">
                      Joined Date
                    </p>
                  </th>
                  <th scope="col" className="px-6 py-4">
                    <p className="border-l-2 border-lightgray-50 text-lightgray-200 px-4">
                      Email Address
                    </p>
                  </th>
                  <th scope="col" className="px-6 py-4">
                    <p className="border-l-2 border-lightgray-50 text-lightgray-200 px-4">
                      Status
                    </p>
                  </th>
                  <th scope="col" className="px-6 py-4 text-center">
                    <p className="border-l-2 border-lightgray-50 text-lightgray-200 px-4">
                      Total Tokens 
                    </p>
                  </th>
                  <th scope="col" className="px-6 py-4"></th>
                </tr>
              </thead>
              <tbody className="rounded-lg">
                {loading ? (
                  <tr>
                    <td colSpan={7} className="text-center py-4">
                      Loading...
                    </td>
                  </tr>
                ) : (
                  users?.map((user) => (
                    <tr
                      key={user?.userId}
                      className="odd:bg-white even:bg-blue-50 border"
                    >
                      <td className="px-6 py-4 font-normal text-sm text-grease-100 whitespace-nowrap">
                        <p className="pl-3">{user?.userId}</p>
                      </td>
                      <td className="px-6 py-4 font-normal text-sm text-grease-100 whitespace-nowrap">
                        <p className="px-4">
                          {user.username ? `${user.username.charAt(0).toUpperCase()}${user.username.slice(1)}` : "UserName"}
                        </p>
                      </td>
                      <td className="px-6 py-4 font-normal text-sm text-grease-100 whitespace-nowrap">
                        <p className="px-4">{new Date(user?.joinedDate).toLocaleDateString("en-GB")}</p>
                      </td>
                      <td className="px-6 py-4 font-normal text-sm text-grease-100 whitespace-nowrap">
                        <p className="px-4">{user?.email}</p>
                      </td>
                      <td className="px-6 py-4 font-normal text-grease-100 whitespace-nowrap">
                        <p
                          className={`mx-4 px-3 py-1 text-white text-xs rounded-full w-fit ${
                            user?.status === "Active" ? "bg-teal-500" : "bg-red-500"
                          }`}
                        >
                          {user?.status || "unknown"}
                        </p>
                      </td>
                      <td className="px-6 py-4 font-normal text-sm text-grease-100 whitespace-nowrap">
                        <p className="px-4 text-center">{user?.totalCredits}</p>
                      </td>
                      <td className="px-6 py-4 font-normal text-sm text-grease-100 whitespace-nowrap">
                        <button
                          onClick={() => openBlockUserModal(user)}
                          className={`
                            rounded-md px-4 py-2 text-xs w-20
                            ${user?.status === "Blocked" ? "text-red-500 border border-red-500" : "text-teal-500 border border-teal-500"}
                          `}
                        >
                          {user?.status === "Blocked" ? "Unblock" : "Block"}
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination Section */}
          <div className="flex flex-wrap items-center justify-between gap-4 mt-4">
            <div className="flex flex-wrap items-center gap-5">
              <p className="text-lightgray-200 text-sm">Rows per page</p>
              <form className="w-fit">
                <select
                  className="block w-full border-none pl-3 pr-10 py-2 rounded-lg bg-lightblue-100 text-sm text-grease-100 placeholder-gray-400 focus:outline-none"
                  value={pageSize}
                  onChange={(e) => {
                    setPageSize(Number(e.target.value));
                    setCurrentPage(1); // Reset to first page when changing page size
                  }}
                >
                  <option value={7}>7</option>
                  <option value={8}>8</option>
                  <option value={9}>9</option>
                  <option value={10}>10</option>
                </select>
              </form>
            </div>

            <div>
              <ul className="flex flex-wrap items-center justify-center my-6 gap-3 text-sm">
                {/* Previous Button */}
                <li>
                  <button
                    disabled={currentPage === 1}
                    className={`flex items-center justify-center w-7 h-7 leading-tight border rounded-md ${
                      currentPage === 1 ? "text-gray-300 border-gray-300" : "text-primaryblue-50 border-primaryblue-50"
                    }`}
                    onClick={() => setCurrentPage(currentPage - 1)}
                  >
                    <span className="sr-only">Previous</span>
                    <i className="st-arrow-right text-xl font-semibold rotate-180"></i>
                  </button>
                </li>

                {/* Page Numbers */}
                {generatePageNumbers().map((pageNum) => (
                  <li key={pageNum}>
                    <button
                      className={`flex items-center justify-center w-7 h-7 rounded-md ${
                        currentPage === pageNum
                          ? "text-white border border-primaryblue-50 bg-primaryblue-50"
                          : "text-primaryblue-50 border border-primaryblue-50"
                      }`}
                      onClick={() => setCurrentPage(pageNum)}
                    >
                      {pageNum}
                    </button>
                  </li>
                ))}

                {/* Next Button */}
                <li>
                  <button
                    disabled={currentPage === totalPages}
                    className={`flex items-center justify-center w-7 h-7 leading-tight border rounded-md ${
                      currentPage === totalPages ? "text-gray-300 border-gray-300" : "text-primaryblue-50 border-primaryblue-50"
                    }`}
                    onClick={() => setCurrentPage(currentPage + 1)}
                  >
                    <span className="sr-only">Next</span>
                    <i className="st-arrow-right text-xl font-semibold"></i>
                  </button>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>
      {/* Render Block User Modal */}
      {selectedUser && (
        <BlockUserModal
          userDetails={selectedUser}
          onClose={closeBlockUserModal}
          onBlockSuccess={() => {
            // Refresh users or update list
            fetchUsers(currentPage, pageSize);
          }}
        />
      )}
    </div>
  );
};

export default UserManagement;
